import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, CalendarClock, UserCog } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ICONS = [<Lock size={20} />, <CalendarClock size={20} />, <UserCog size={20} />];

export function Corporate() {
  const { t } = useI18n();
  const c = t.corporate;
  const [form, setForm] = useState({
    business_name: "", sector: "", estimated_seats: "10",
    destinations: "", contact_name: "", contact_role: "", business_email: "", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/corporate", { ...form, estimated_seats: parseInt(form.estimated_seats || "1", 10) });
      toast.success(c.success);
      setForm({ business_name: "", sector: "", estimated_seats: "10", destinations: "", contact_name: "", contact_role: "", business_email: "", phone: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || c.error);
    } finally { setLoading(false); }
  };

  const fieldCls = "rounded-none border-0 border-b border-white/20 bg-transparent px-0 text-white focus-visible:ring-0 focus-visible:border-gold";

  return (
    <section id="corporate" className="bg-midnight text-white py-24 md:py-32 grain border-t border-white/10">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <p className="overline text-champagne">{c.overline}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.02]">{c.title}</h2>
            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md">{c.body}</p>
            <div className="mt-10 space-y-6">
              {c.benefits.map((b, i) => (
                <div key={i} className="flex gap-4" data-testid={`corp-benefit-${i}`}>
                  <div className="text-champagne mt-1">{ICONS[i]}</div>
                  <div>
                    <div className="font-serif text-xl">{b.t}</div>
                    <div className="text-white/50 text-sm mt-1 max-w-xs">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="corporate-form">
              <CField label={c.businessName}><Input required data-testid="corp-business-name" value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} className={fieldCls} /></CField>
              <CField label={c.sector}><Input required data-testid="corp-sector" value={form.sector} onChange={(e) => set("sector")(e.target.value)} className={fieldCls} /></CField>
              <CField label={c.seats}><Input type="number" min="1" required data-testid="corp-seats" value={form.estimated_seats} onChange={(e) => set("estimated_seats")(e.target.value)} className={fieldCls} /></CField>
              <CField label={c.contactName}><Input required data-testid="corp-contact-name" value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} className={fieldCls} /></CField>
              <CField label={c.contactRole}><Input required data-testid="corp-contact-role" value={form.contact_role} onChange={(e) => set("contact_role")(e.target.value)} className={fieldCls} /></CField>
              <CField label={c.email}><Input type="email" required data-testid="corp-email" value={form.business_email} onChange={(e) => set("business_email")(e.target.value)} className={fieldCls} /></CField>
              <div className="sm:col-span-2">
                <CField label={c.phone}><Input required data-testid="corp-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className={fieldCls} /></CField>
              </div>
              <div className="sm:col-span-2">
                <CField label={c.destinations}>
                  <Textarea rows={2} data-testid="corp-destinations" value={form.destinations} onChange={(e) => set("destinations")(e.target.value)} placeholder={c.destinationsPlaceholder} className={fieldCls + " resize-none placeholder:text-white/30"} />
                </CField>
                <p className="mt-2 text-xs text-champagne/70 leading-relaxed" data-testid="corp-destinations-note">{c.destinationsNote}</p>
              </div>
              <div className="sm:col-span-2 mt-4">
                <button type="submit" disabled={loading} data-testid="corp-submit"
                  className="w-full sm:w-auto rounded-full bg-gold px-10 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-midnight transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60">
                  {loading ? c.sending : c.submit}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CField({ label, children }) {
  return <div className="flex flex-col gap-2"><Label className="overline text-white/50">{label}</Label>{children}</div>;
}
